/**
 * Developer Tools - Browser Console Utilities
 *
 * Access these tools from the browser console:
 * - window.devTools.clearAllCaches()
 * - window.devTools.resetApp()
 * - window.devTools.showDebugInfo()
 * - window.devTools.testBlueLineHover()
 */

import { barberDataService } from '../services/barberDataService';
import { appointmentService } from '../services/appointmentService';
import { clientDataService } from '../services/clientDataService';
import { db } from '../config/firebase';
import { collection, getDocs, deleteDoc, doc, query } from 'firebase/firestore';
import { ymd } from '../shared/dateUtils';

export class DevTools {

  /**
   * Clear all localStorage caches and reload the app
   */
  async clearAllCaches(): Promise<void> {
    console.log('🧹 Clearing all caches...');

    const cacheKeys = [
      'slikpos_barbers',
      'slikpos_appointments',
      'slikpos_clients',
      'slikpos_services'
    ];

    cacheKeys.forEach(key => {
      if (localStorage.getItem(key)) {
        localStorage.removeItem(key);
        console.log(`  ✅ Cleared: ${key}`);
      }
    });

    console.log('🔄 Reloading app...');
    window.location.reload();
  }

  /**
   * Full app reset - clears everything and reloads
   */
  async resetApp(): Promise<void> {
    console.log('🔄 Performing full app reset...');

    // Clear all localStorage
    const itemsCleared = localStorage.length;
    localStorage.clear();
    console.log(`  ✅ Cleared ${itemsCleared} localStorage items`);

    // Clear sessionStorage
    sessionStorage.clear();
    console.log('  ✅ Cleared sessionStorage');

    console.log('🔄 Reloading app...');
    window.location.reload();
  }

  /**
   * Show current app state and debug information
   */
  async showDebugInfo(): Promise<void> {
    console.log('📊 === SLIK POS Debug Information ===\n');

    // Barbers (debug dump — include archived)
    const barbers = barberDataService.getAllBarbers(true);
    console.log('👨‍💼 Barbers:', barbers.length);
    barbers.forEach(b => {
      console.log(`  - ${b.firstName} ${b.lastName} (${b.id}) - Status: ${b.status}`);
    });

    // Active barbers
    const activeBarbers = barberDataService.getActiveBarbers();
    console.log(`\n✅ Active Barbers: ${activeBarbers.length}`);

    // Barber stats
    const stats = barberDataService.getBarberStats();
    console.log('\n📈 Barber Statistics:', stats);

    // Appointments
    const today = new Date();
    const todayString = ymd(today);
    const appointments = await appointmentService.getAppointmentsByDate(todayString);
    console.log(`\n📅 Appointments Today: ${appointments.length}`);

    // Clients
    const clients = clientDataService.getAllClients();
    console.log(`\n👥 Clients: ${clients.length}`);

    // LocalStorage
    console.log('\n💾 LocalStorage Keys:');
    Object.keys(localStorage).forEach(key => {
      if (key.startsWith('slikpos_')) {
        const value = localStorage.getItem(key);
        const size = value ? (value.length / 1024).toFixed(2) : 0;
        console.log(`  - ${key}: ${size} KB`);
      }
    });

    console.log('\n=== End Debug Info ===\n');
  }

  /**
   * Test the blue line hover indicator
   */
  testBlueLineHover(): void {
    console.log('🔵 Testing Blue Line Hover Indicator...');
    console.log('\nTo test:');
    console.log('1. Navigate to Calendar (Day View)');
    console.log('2. Move your mouse over the calendar grid');
    console.log('3. You should see:');
    console.log('   - A blue time indicator line following your mouse');
    console.log('   - The time updating as you move');
    console.log('   - The line updating when you scroll');
    console.log('\nIf you don\'t see the blue line:');
    console.log('1. Make sure you\'re in DAY view (not Week or Month)');
    console.log('2. Make sure your mouse is over the calendar grid (not the time labels)');
    console.log('3. Check the console for any errors');
    console.log('\nCurrent View Mode: Check the calendar view selector');
  }

  /**
   * Force reload barbers from Firebase
   */
  async reloadBarbers(): Promise<void> {
    console.log('🔄 Reloading barbers from Firebase...');

    localStorage.removeItem('slikpos_barbers');
    const barbers = await barberDataService.getAllBarbersAsync(true); // reload debug: include archived

    console.log(`✅ Loaded ${barbers.length} barbers:`);
    barbers.forEach(b => {
      console.log(`  - ${b.firstName} ${b.lastName} (${b.status})`);
    });

    console.log('🔄 Refreshing page to apply changes...');
    window.location.reload();
  }

  /**
   * Quick fix for common issues
   */
  async quickFix(issue: 'barbers' | 'cache' | 'all'): Promise<void> {
    console.log(`🔧 Running quick fix for: ${issue}`);

    switch (issue) {
      case 'barbers':
        await this.reloadBarbers();
        break;
      case 'cache':
        await this.clearAllCaches();
        break;
      case 'all':
        await this.resetApp();
        break;
      default:
        console.log('❌ Unknown issue type. Use: "barbers", "cache", or "all"');
    }
  }

  /**
   * Clean up duplicate appointment records in Firebase
   * This fixes the issue where status updates don't persist
   */
  async cleanupDuplicates(): Promise<{ success: boolean; message: string; removed: number }> {
    console.log('🔍 Scanning for duplicate appointments...\n');

    try {
      const appointmentsRef = collection(db, 'appointments');
      const q = query(appointmentsRef);
      const snapshot = await getDocs(q);

      console.log(`Total appointments in Firebase: ${snapshot.size}\n`);

      // Group appointments by clientName + date + time
      const appointmentGroups = new Map<string, any[]>();

      snapshot.forEach((docSnap) => {
        const data = docSnap.data();
        const appointment = {
          id: docSnap.id,
          ...data
        };

        // Create a unique key based on client, date, and time
        const key = `${data.clientName}-${data.date}-${data.time}`;

        if (!appointmentGroups.has(key)) {
          appointmentGroups.set(key, []);
        }
        appointmentGroups.get(key)!.push(appointment);
      });

      // Find duplicates
      const duplicates: Array<{ key: string; appointments: any[] }> = [];
      appointmentGroups.forEach((group, key) => {
        if (group.length > 1) {
          duplicates.push({ key, appointments: group });
        }
      });

      if (duplicates.length === 0) {
        console.log('✅ No duplicates found!');
        return {
          success: true,
          message: 'No duplicates found',
          removed: 0
        };
      }

      console.log(`⚠️  Found ${duplicates.length} duplicate groups:\n`);

      // Display duplicates
      duplicates.forEach(({ key, appointments }, index) => {
        console.log(`\n${index + 1}. Duplicate: ${key}`);
        appointments.forEach((apt, i) => {
          console.log(`   [${i + 1}] ID: ${apt.id}`);
          console.log(`       Status: ${apt.status}`);
          console.log(`       Created: ${apt.createdAt?.toDate?.() || apt.createdAt}`);
        });
      });

      console.log('\n\n🗑️  Removing duplicates (keeping the most recent one)...\n');

      let removedCount = 0;

      // Remove duplicates
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      for (const { key, appointments } of duplicates) {
        // Sort by creation date, keep the most recent
        const sorted = appointments.sort((a, b) => {
          const dateA = a.createdAt?.toDate?.() || new Date(a.createdAt);
          const dateB = b.createdAt?.toDate?.() || new Date(b.createdAt);
          return dateB.getTime() - dateA.getTime(); // Most recent first
        });

        const toKeep = sorted[0];
        const toDelete = sorted.slice(1);

        console.log(`Keeping: ${toKeep.id} (${toKeep.clientName})`);

        for (const apt of toDelete) {
          console.log(`  Deleting: ${apt.id}`);
          await deleteDoc(doc(db, 'appointments', apt.id));
          removedCount++;
        }
      }

      console.log('\n✅ Cleanup complete!');
      console.log(`Removed ${removedCount} duplicate appointments`);

      return {
        success: true,
        message: 'Cleanup complete',
        removed: removedCount
      };
    } catch (error) {
      console.error('❌ Error during cleanup:', error);
      return {
        success: false,
        message: `Error: ${error instanceof Error ? error.message : 'Unknown error'}`,
        removed: 0
      };
    }
  }

  /**
   * Show available commands
   */
  help(): void {
    console.log('🛠️  === SLIK POS Developer Tools ===\n');
    console.log('Available Commands:\n');
    console.log('devTools.showDebugInfo()          - Show current app state');
    console.log('devTools.clearAllCaches()         - Clear all caches and reload');
    console.log('devTools.resetApp()               - Full app reset');
    console.log('devTools.reloadBarbers()          - Reload barbers from Firebase');
    console.log('devTools.cleanupDuplicates()      - Remove duplicate appointments');
    console.log('devTools.testBlueLineHover()      - Test blue line hover indicator');
    console.log('devTools.quickFix("barbers")      - Quick fix for barber issues');
    console.log('devTools.quickFix("cache")        - Quick fix for cache issues');
    console.log('devTools.quickFix("all")          - Reset everything');
    console.log('devTools.help()                   - Show this help message\n');
  }
}

// Create singleton instance
export const devTools = new DevTools();

// Expose to window for console access
if (typeof window !== 'undefined') {
  (window as any).devTools = devTools;
  console.log('🛠️  DevTools loaded! Type "devTools.help()" for available commands.');
}
